import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Loader2, Search, Mail } from "lucide-react";
import { toast } from "sonner";

const GiftBalance = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [code, setCode] = useState((params.get("code") || "").toUpperCase());
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const lookUp = async (e) => {
    e?.preventDefault();
    if (!code || !email) {
      setError("Enter both the code and the email the gift was sent to.");
      return;
    }
    setLoading(true); setError(""); setResult(null);
    try {
      const { data } = await api.post("/gifts/balance", { code, recipient_email: email });
      setResult(data);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not find that gift.";
      if (err?.response?.status === 423) setError("This code is temporarily locked. Please email support.");
      else if (err?.response?.status === 429) setError("Too many tries. Wait a few minutes before trying again.");
      else setError(typeof detail === "string" ? detail : "Could not find that gift.");
    } finally {
      setLoading(false);
    }
  };

  // Auto-check if a code is in the URL
  useEffect(() => {
    if (code && !result) {
      // Only auto-check once we have an email too
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="grain min-h-screen" data-testid="gift-balance-page">
      <div className="max-w-xl mx-auto px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#a39797] hover:text-[#d4af37] mb-10"
          data-testid="balance-back">
          <ArrowLeft size={16} /> back to the file
        </Link>
        <div className="font-hand text-[#d4af37] rotate-[-1deg] text-2xl mb-2">balance check</div>
        <h1 className="font-display text-4xl mb-4">Check a gift card.</h1>
        <p className="text-[#a39797] mb-6">Enter the code and the email we delivered it to.</p>

        <form onSubmit={lookUp} className="space-y-4">
          <div>
            <label className="block text-xs tracking-widest text-[#a39797] mb-1" htmlFor="gc-code">CODE</label>
            <input id="gc-code" className="input-noir font-mono tracking-wider uppercase"
              placeholder="XXXX-XXXX-XXXX-XXXX" value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 16))}
              data-testid="input-balance-code" />
          </div>
          <div>
            <label className="block text-xs tracking-widest text-[#a39797] mb-1" htmlFor="gc-email">EMAIL</label>
            <input id="gc-email" type="email" className="input-noir"
              placeholder="The email the gift was sent to"
              value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="input-balance-email" />
          </div>
          <button type="submit" disabled={loading} className="btn-primary disabled:opacity-50"
            data-testid="btn-check-balance">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
            {loading ? "checking…" : "Check balance"}
          </button>
          {error && (
            <div className="text-sm text-[#ff6b6b]" data-testid="balance-error">{error}</div>
          )}
        </form>

        {result && (
          <div className="card-noir p-6 mt-8" data-testid="balance-result">
            {result.status === "voided" ? (
              <>
                <div className="text-sm text-[#ff6b6b]">This gift has been voided.</div>
                <p className="text-xs text-[#a39797] mt-2">If this looks wrong, please contact support.</p>
              </>
            ) : (
              <>
                <div className="font-mono text-xl tracking-wider mb-2">{result.pretty_code}</div>
                <div className="font-display text-4xl text-[#f4f0ec] mb-1" data-testid="balance-amount">
                  {result.kind === "gift_case" ? `Case gift` : `$${(result.balance_cents / 100).toFixed(2)}`}
                </div>
                <div className="text-xs tracking-widest text-[#a39797] mb-4">
                  {result.kind === "gift_case" ? `This gift unlocks ${result.case_id}.` : "Spend it on anything, any time."}
                </div>
                {result.message && (
                  <div className="italic text-[#a39797] text-sm border-l-2 border-[#d4af37] pl-3 mb-4">
                    "{result.message}" <span className="not-italic text-xs">— {result.sender_name}</span>
                  </div>
                )}
                <button onClick={() => navigate(`/checkout?gift=${encodeURIComponent(code)}`)}
                  className="btn-primary text-sm" data-testid="btn-redeem-now">
                  <Mail size={14} /> Redeem at checkout
                </button>
                <div className="text-xs text-[#a39797] mt-3">
                  Codes never expire. One promo code per order; apply your gift at checkout.
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GiftBalance;
